// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { InvoiceDetail } from './invoice-detail';
import { InvoiceStatus } from './invoice-status';
import { ItemDetail } from './item-detail';
import { JoHeader } from './jo-header';
import { ManifestGroupInvoice } from './manifest-group-invoice';
import { ShipBin } from './ship-bin';
import { Shipment } from './shipment';

/// <code-import> Place custom imports between <code-import> tags
import _ from "lodash";
import { InvoiceStatusCode } from "../enums/invoice-status-code";
import { UtilFns } from "@/shared/util-fns";
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class InvoiceHeader extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  /** Global freight markup ratio (0.15 = 15%), set by DbQueryService.cacheAll() after loading Configs (Freight/MarkupPct). */
  static freightMarkupRatio = 0;

  private applyFreightMarkup(amt: number): number {
    const ratio = InvoiceHeader.freightMarkupRatio ?? 0;
    return ratio > 0 ? amt * (1 + ratio) : amt;
  }

  get isVoucher() {
    return (
      this.invoiceStatusId === InvoiceStatusCode.Voucher ||
      this.invoiceStatusId === InvoiceStatusCode.VoucherLabeled ||
      this.invoiceStatusId === InvoiceStatusCode.Printed ||
      this.invoiceStatusId === InvoiceStatusCode.VoidError
    );
  }

  get isLabelled() {
    return this.invoiceStatusId === InvoiceStatusCode.VoucherLabeled || this.invoiceStatusId === InvoiceStatusCode.Printed;
  }

  // requires that InvoiceDetails and each accompanying JoDetail and mods be available
  calcExtendedAmt() {
    if (!this.invoiceDetails) {
      return null;
    }
    return _.sum(this.invoiceDetails.map((invd) => (invd.joDetail?.unitTotalAmt ?? 0) * (invd.shipQty ?? 0)));
  }

  calcExtendedReissueAmt() {
    if (!this.invoiceDetails) {
      return null;
    }
    const chargeAddons = this.joHeader?.chargeAddonsOnReissuance ?? false;
    return _.sum(this.invoiceDetails.map((invd) => invd.calcExtendedReissueAmt(chargeAddons)));
  }

  calcExtendedQty() {
    if (!this.invoiceDetails) {
      return null;
    }
    return _.sum(this.invoiceDetails.map((invd) => invd.shipQty || 0));
  }

  calcExtendedReissueQty() {
    if (!this.invoiceDetails) {
      return null;
    }
    return _.sum(this.invoiceDetails.map((invd) => invd.redistributeQty || 0));
  }

  get hasFreight() {
    if (this.joHeader.doesAccountPayFreight === false) {
      return false;
    } else if (this.shipment?.primaryInvoiceHeaderId === this.id) {
      return this.shipment?.freightAmt != null;
    } else {
      return true;
    }
  }

  /** Whether another invoice on the JO has freight added.  Used with shouldChargeFreightOnFirstShipmentOnly. Requires shipment to have been queried. */
  get otherInvoiceHasFreight() {
    if (this.joHeader.doesAccountPayFreight === false) {
      return false;
    } else {
      return this.joHeader.invoiceHeaders.some((ih) => ih.id != this.id && ih.freightAmt > 0);
    }
  }

  get hasShippingLabel() {
    return this.shipment?.labelApiIdentifier != null;
  }

  /** True when this invoice's shipment is a counter pickup rather than a carrier shipment.
   *  A pickup shipment never gets a freight quote or a shipping label, so labelApiIdentifier
   *  stays null permanently -- check this before treating a null label as "not yet labeled". */
  get isPickup() {
    return this.shipment?.isPickup === true;
  }

  /**
   * Determines if this invoice is a "first shipment" invoice (i.e., ends with "-01").
   *
   * Invoice IDs follow the format: {JoHeaderId}-{SequenceNumber}
   * - "-01" = First shipment for the Job Order (eligible for freight when shouldChargeFreightOnFirstShipmentOnly = true)
   * - "-02", "-03", etc. = Backorder shipments (NOT eligible for freight when shouldChargeFreightOnFirstShipmentOnly = true)
   *
   * @returns true if this invoice ID ends with "-01", false otherwise
   */
  get isFirstShipmentInvoice(): boolean {
    return this.id?.endsWith('-01') ?? false;
  }

  /**
   * Calculates the proportional freight amount when a shipment contains mixed -01 and backorder invoices
   * and shouldChargeFreightOnFirstShipmentOnly = true.
   *
   * THE PROBLEM:
   * When multiple invoices share a single physical box (Shipment), ShipEngine returns ONE freight amount
   * for the entire box. However, if shouldChargeFreightOnFirstShipmentOnly = true, we should only charge
   * freight for the portion of the box that belongs to -01 (first shipment) invoices.
   *
   * THE SOLUTION:
   * Calculate freight proportionally based on invoice subtotals:
   * 1. Sum subtotals of ONLY -01 invoices in the shipment (eligible invoices)
   * 2. Sum subtotals of ALL invoices in the shipment
   * 3. Freight to charge = totalFreight × (eligibleSubtotal / totalSubtotal)
   *
   * EXAMPLE:
   * Shipment contains: 1850846-01 ($200), 1850152-04 ($150), 1849904-03 ($150)
   * Total freight from ShipEngine: $25
   * Only 1850846-01 is eligible (ends with -01)
   * Eligible subtotal: $200, Total subtotal: $500
   * Proportional freight: $25 × ($200 / $500) = $25 × 40% = $10
   *
   * @requires shipment and shipment.invoiceHeaders must be loaded
   * @returns The proportional freight amount to charge on this shipment
   */
  calcProportionalFreight(): number {
    if (!this.shipment?.invoiceHeaders?.length) {
      return 0;
    }

    const invoices = this.shipment.invoiceHeaders;
    const totalFreight = this.shipment.freightAmt ?? 0;

    // Calculate subtotal for ALL invoices in this shipment
    const totalSubtotal = invoices.reduce((sum, inv) => sum + (inv.subtotalAmt ?? 0), 0);

    // Calculate subtotal for ONLY -01 (first shipment) invoices
    const eligibleSubtotal = invoices
      .filter(inv => inv.isFirstShipmentInvoice)
      .reduce((sum, inv) => sum + (inv.subtotalAmt ?? 0), 0);

    // If no eligible invoices or no subtotal, return 0
    if (eligibleSubtotal === 0 || totalSubtotal === 0) {
      return 0;
    }

    // Calculate proportional freight
    const proportion = eligibleSubtotal / totalSubtotal;
    return totalFreight * proportion;
  }

  /**
   * Calculates the freight amount to apply to THIS invoice.
   *
   * DECISION TREE:
   * 1. If invoice was migrated from old system → use existing freightAmt (don't recalculate)
   * 2. If account doesn't pay freight → 0
   * 3. If using third-party carrier account → 0 (billed to third party)
   * 4. If NOT the Primary Invoice → 0 (freight only goes on Primary Invoice)
   * 5. If this IS the Primary Invoice:
   *    a. If shouldChargeFreightOnFirstShipmentOnly = false → full shipment freight
   *    b. If shouldChargeFreightOnFirstShipmentOnly = true:
   *       - If ALL invoices in shipment are -01 → full shipment freight
   *       - If MIXED invoices (-01 and backorders) → proportional freight based on -01 subtotals
   *
   * IMPORTANT CONCEPTS:
   * - Primary Invoice: The invoice designated to RECEIVE the freight charge (set via checkbox in UI)
   * - First Shipment Invoice (-01): Determines ELIGIBILITY for freight charges
   * - These are DIFFERENT! A -03 invoice can be the Primary Invoice, but -03 is NOT eligible for freight.
   *
   * @requires shipment, shipment.invoiceHeaders, and joHeader must be loaded
   * @returns The freight amount to charge on this invoice
   */
  calcShipFreightAmt(): number {
    // Always use migrated freight for any data migrated from old posted invoices.
    if (this.wasMigrated) {
      return this.freightAmt ?? 0;
    }

    // Account-owned invoices have no shipment; freight is entered manually
    // during end-of-day processing and stored directly on the header.
    if (this.joHeader.isAccountOwnedOrder) {
      return this.freightAmt ?? 0;
    }

    // Counter pickup - no carrier was involved, so no freight quote exists
    if (this.isPickup) {
      return 0;
    }

    // Account doesn't pay freight
    if (this.joHeader.doesAccountPayFreight === false) {
      return 0;
    }

    // Third-party carrier account - freight billed to third party, not on invoice
    if (this.joHeader.carrierAccountNumber) {
      return 0;
    }

    // Only the Primary Invoice receives the freight charge
    if (this.shipment?.primaryInvoiceHeaderId !== this.id) {
      return 0;
    }

    // This IS the Primary Invoice - calculate the appropriate freight amount
    const totalFreight = this.shipment.freightAmt ?? 0;

    // If shouldChargeFreightOnFirstShipmentOnly is false, apply full freight
    if (!this.joHeader.shouldChargeFreightOnFirstShipmentOnly) {
      return this.applyFreightMarkup(totalFreight);
    }

    // shouldChargeFreightOnFirstShipmentOnly = true
    // Check if all invoices in this shipment are -01 invoices
    const invoices = this.shipment.invoiceHeaders ?? [];
    const allAreFirstShipment = invoices.every(inv => inv.isFirstShipmentInvoice);

    if (allAreFirstShipment) {
      // All invoices are -01, so full freight applies
      return this.applyFreightMarkup(totalFreight);
    }

    // Mixed invoices: some -01, some backorders
    // Calculate proportional freight based on -01 invoice subtotals only
    return this.applyFreightMarkup(this.calcProportionalFreight());
  }

  /**
   * Returns a human-readable description of how freight is calculated for this invoice.
   * Used for display/tooltip purposes to explain the freight calculation logic.
   *
   * @requires shipment, shipment.invoiceHeaders, and joHeader must be loaded
   */
  get shipFreightInfo(): string {
    if (this.wasMigrated) {
      return "Invoice contains freight cost migrated from the old system.";
    }
    if (this.shipment == null) {
      if (this.joHeader.isAccountOwnedOrder) {
        return "Account purchase order - no freight associated.";
      } else {
        return "Invoice does not yet include freight.";
      }
    }
    if (this.isPickup) {
      return "Customer pickup - no carrier freight on this invoice.";
    }
    if (this.joHeader.doesAccountPayFreight === false) {
      return "Freight is not charged on this invoice.";
    }
    if (this.joHeader.carrierAccountNumber) {
      const amt = UtilFns.fmtCurrency(this.shipment.freightAmt);
      return `Freight ${amt} is charged to third-party ${this.joHeader.carrierAccountName} account.`;
    }

    // Not the Primary Invoice - freight appears elsewhere
    if (this.shipment.primaryInvoiceHeaderId !== this.id) {
      return "The freight for this invoice appears on Invoice: " + this.shipment.primaryInvoiceHeaderId;
    }

    // This IS the Primary Invoice
    const relatedInvoices = this.shipment.invoiceHeaders ?? [];
    const totalFreight = this.shipment.freightAmt ?? 0;
    const totalFreightFmt = UtilFns.fmtCurrency(totalFreight);
    const chargedFmt = UtilFns.fmtCurrency(this.calcShipFreightAmt());

    // Single invoice in shipment
    if (relatedInvoices.length <= 1) {
      return `Invoice includes freight ${chargedFmt} for this invoice alone.`;
    }

    // Multiple invoices in shipment
    const invoiceIds = relatedInvoices.map((inv) => inv.id).join(", ");

    // Check if proportional calculation applies
    if (this.joHeader.shouldChargeFreightOnFirstShipmentOnly) {
      const allAreFirstShipment = relatedInvoices.every(inv => inv.isFirstShipmentInvoice);

      if (!allAreFirstShipment) {
        // Proportional calculation was used
        const eligibleInvoices = relatedInvoices.filter(inv => inv.isFirstShipmentInvoice);
        const eligibleIds = eligibleInvoices.map(inv => inv.id).join(", ");

        if (eligibleInvoices.length === 0) {
          return `Shipment includes invoices: ${invoiceIds}. No -01 invoices eligible for freight (shouldChargeFreightOnFirstShipmentOnly = true). Freight: $0.`;
        }

        return `Base shipment freight ${totalFreightFmt} shared by: ${invoiceIds}. ` +
          `Only first-shipment invoices (${eligibleIds}) are eligible. ` +
          `Proportional freight charged: ${chargedFmt}.`;
      }
    }

    // Full freight applies (either shouldChargeFreightOnFirstShipmentOnly = false, or all are -01)
    return `Invoice includes freight ${chargedFmt} from shipment containing: ${invoiceIds}.`;
  }

  /** Total of details + taxes + handling + shipping - payments. May be negative if prepaymentAmt is large. */
  calcTotalAmt() {
    return (
      (this.calcExtendedAmt() ?? 0) +
      (this.calcExtendedReissueAmt() ?? 0) +
      (this.taxAmt ?? 0) +
      (this.orderHandlingAmt ?? 0) +
      this.calcShipFreightAmt() -
      (this.prepaymentAmt ?? 0) -
      (this.pCardAmt ?? 0)
    );
  }

  /** calcTotalAmt() but rounded to 2 decimal places */
  roundedTotalAmt() {
    return +this.calcTotalAmt().toFixed(2);
  }

  get isPosted() {
    return this.invoiceStatusId === InvoiceStatusCode.InvoicePosted;
  }

  get isPostedOrQueued() {
    return this.invoiceStatusId === InvoiceStatusCode.InvoicePosted || this.invoiceStatusId === InvoiceStatusCode.AwaitPost;
  }

  get isNotPosted() {
    return !this.isPosted;
  }

  get isSubInvoice() {
    if (this.shipment == null) {
      return null;
    } else {
      return this.shipment.primaryInvoiceHeaderId !== this.id;
    }
  }

  // assumes that shipment and shipment.invoiceHeaders have been fetched
  get isPrimaryInvoice() {
    return this.id === this.shipment?.primaryInvoiceHeaderId;
  }
  set isPrimaryInvoice(value) {
    if (!this.shipment) {
      return;
    }
    if (value) {
      this.shipment.primaryInvoiceHeaderId = this.id;
    } else if (this.id === this.shipment.primaryInvoiceHeaderId) {
      // idiom for exit after first success
      this.shipment.invoiceHeaders.some((inv) => {
        const ok = this.id !== inv.id;
        if (ok) {
          this.shipment.primaryInvoiceHeaderId = inv.id;
        }
        return ok;
      });
    }
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** String(10) key */
  id!: string;
  /** DateTimeOffset */
  crtnTs?: Date;
  /** String(50) */
  deliveredBy?: string;
  /** DateTime */
  dueDate?: Date;
  /** Decimal */
  freightAmt?: number;
  /** String(100) */
  gLInvoiceId?: string;
  /** DateTime */
  invoiceDate?: Date;
  /** String(10) FK invoiceStatus */
  invoiceStatusId?: string;
  /** Int32 FK joHeader */
  joHeaderId!: number;
  /** String(100) */
  manifestName?: string;
  /** Decimal */
  orderHandlingAmt?: number;
  /** Decimal */
  pCardAmt?: number;
  /** String(100) */
  pCardName?: string;
  /** String(50) */
  pCardTransactionId?: string;
  /** DateTimeOffset */
  pdfSavedAt?: Date;
  /** Decimal */
  prepaymentAmt?: number;
  /** DateTimeOffset */
  printedAt?: Date;
  /** Binary */
  rowVersion!: any;
  /** String(255) */
  savedToPdf?: string;
  /** Int32 FK shipBin */
  shipBinId?: number;
  /** DateTime */
  shipExpirationDate?: Date;
  /** DateTimeOffset */
  shipTs?: Date;
  /** Int32 FK shipment */
  shipmentId?: number;
  /** String(4) */
  shippingExt?: string;
  /** String(25) */
  shippingTelephone?: string;
  /** Decimal */
  subtotalAmt?: number;
  /** Decimal */
  taxAmt?: number;
  /** Decimal */
  totalAmt?: number;
  /** Boolean */
  wasMigrated!: boolean;
  /** Inv FK accountPurchaseInvoiceHeaderId */
  accountPurchaseItemDetails!: ItemDetail[];
  /** Inv FK invoiceHeaderId */
  invoiceDetails!: InvoiceDetail[];
  /** FK invoiceStatusId */
  invoiceStatus!: InvoiceStatus;
  /** Inv FK invoiceHeaderId */
  itemDetails!: ItemDetail[];
  /** FK joHeaderId */
  joHeader!: JoHeader;
  /** Inv FK invoiceHeaderId */
  manifestGroupInvoices!: ManifestGroupInvoice[];
  /** FK shipBinId */
  shipBin!: ShipBin;
  /** FK shipmentId */
  shipment!: Shipment;
}

